import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { useUISettings } from '../context/UISettingsContext';
import AppIcon from './AppIcon';
import logoFallback from '../assets/koc-logo.svg';
import { BRAND_NAME, BRAND_SHORT } from '../constants/brand';
import { getOfflineQueueCount, processOfflineQueue, subscribeOfflineQueue } from '../utils/offlineQueue';
import {
  getOfflinePhotoQueueCount,
  processOfflinePhotoQueue,
  subscribeOfflinePhotoQueue,
} from '../utils/offlinePhotoQueue';
import {
  ROLE_IDS,
  canAccessAccountingPanel,
  canAccessAdminPermissions,
  canManageUserDirectory,
  hasEffectivePermission,
  isAccountant as isAccountantRole,
} from '../utils/accessControl';
import '../styles/Layout.css';

const API_BASE = API_BASE_URL;

const EMOJI_ICONS = {
  dashboard: '🏠',
  projects: '📁',
  tasks: '📋',
  construction: '🧱',
  puantaj: '⏱️',
  photos: '📸',
  dailyReports: '📝',
  checklists: '✅',
  changeOrders: '🧾',
  operationsCenter: '🛠️',
  nonconformities: '⚠️',
  teams: '👷',
  resources: '📦',
  users: '👥',
  reports: '📊',
  finance: '💼',
  bell: '🔔',
  admin: '☑️',
  settings: '⚙️',
};

const MOBILE_BREAKPOINT = 768;

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth > MOBILE_BREAKPOINT : true));
  const [isMobileView, setIsMobileView] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false));
  const [logoSrc, setLogoSrc] = useState(`${process.env.PUBLIC_URL || ''}/koc_logo_r4.png`);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [isStandaloneMode, setIsStandaloneMode] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [pushPromptBusy, setPushPromptBusy] = useState(false);
  const [pushPromptMessage, setPushPromptMessage] = useState('');
  const [canPromptPush, setCanPromptPush] = useState(false);
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const [offlineSyncMessage, setOfflineSyncMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, permissions } = useAuth();
  const { iconMode } = useUISettings();

  const roleId = Number(user?.role_id);
  const isAccountant = isAccountantRole(user);
  const canSeeUsers = canManageUserDirectory(user, permissions);
  const canSeeAccounting = canAccessAccountingPanel(user, permissions);
  const canSeeAdminPermissions = canAccessAdminPermissions(user);
  const canSeeReports = hasEffectivePermission(user, permissions, 'can_view_reports');
  const canSeeTeams = hasEffectivePermission(user, permissions, 'can_manage_teams') || [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.SITE_SUPERVISOR].includes(roleId);
  const canSeeResources = hasEffectivePermission(user, permissions, 'can_manage_resources') || [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.SITE_SUPERVISOR].includes(roleId);

  const navItems = useMemo(() => {
    if (isAccountant) {
      return [
        { section: 'Muhasebe', to: '/accounting', icon: 'finance', label: 'Muhasebe Paneli' },
        { section: 'Hesap', to: '/settings', icon: 'settings', label: 'Ayarlar' },
      ];
    }

    const base = [
      { section: 'Ana Menü', to: '/', icon: 'dashboard', label: 'Pano' },
      { section: 'Ana Menü', to: '/projects', icon: 'projects', label: 'Projeler' },
      { section: 'Operasyonlar', to: '/tasks', icon: 'tasks', label: 'Görevler' },
      { section: 'Operasyonlar', to: '/change-orders', icon: 'changeOrders', label: 'Değişiklik Emirleri' },
      { section: 'Operasyonlar', to: '/nonconformities', icon: 'construction', label: 'Uygunsuzluklar' },
      { section: 'Operasyonlar', to: '/puantaj', icon: 'puantaj', label: 'Puantaj' },
      { section: 'Operasyonlar', to: '/photos', icon: 'photos', label: 'Fotoğraflar' },
      { section: 'Operasyonlar', to: '/daily-reports', icon: 'dailyReports', label: 'Günlük Raporlar' },
      { section: 'Operasyonlar', to: '/checklists', icon: 'checklists', label: 'Checklistler' },
      { section: 'Operasyonlar', to: '/operations-center', icon: 'operationsCenter', label: 'Operasyon Merkezi' },
      { section: 'Yönetim', to: '/settings', icon: 'settings', label: 'Ayarlar' },
    ];

    if (canSeeTeams) {
      base.push({ section: 'Yönetim', to: '/teams', icon: 'teams', label: 'Ekipler' });
    }
    if (canSeeResources) {
      base.push({ section: 'Yönetim', to: '/resources', icon: 'resources', label: 'Kaynaklar' });
    }
    if (canSeeUsers) {
      base.push({ section: 'Yönetim', to: '/users', icon: 'users', label: 'Kullanıcılar' });
    }
    if (canSeeReports) {
      base.push({ section: 'Yönetim', to: '/reports', icon: 'reports', label: 'Raporlar' });
    }
    if (canSeeAccounting) {
      base.push({ section: 'Yönetim', to: '/accounting', icon: 'finance', label: 'Muhasebe Paneli' });
    }
    if (canSeeAdminPermissions) {
      base.push({ section: 'Yönetim', to: '/admin-permissions', icon: 'admin', label: 'Admin Yetkileri' });
    }
    return base;
  }, [canSeeAccounting, canSeeAdminPermissions, canSeeReports, canSeeResources, canSeeTeams, canSeeUsers, isAccountant]);

  const groupedNav = useMemo(() => {
    return navItems.reduce((acc, item) => {
      if (!acc[item.section]) acc[item.section] = [];
      acc[item.section].push(item);
      return acc;
    }, {});
  }, [navItems]);

  const base64UrlToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/notifications?limit=8`);
      setNotifications(Array.isArray(response.data?.notifications) ? response.data.notifications : []);
      setUnreadCount(Number(response.data?.unread_count || 0));
    } catch (error) {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, []);

  const syncOfflineQueueNow = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (!window.navigator.onLine) {
      setOfflineSyncMessage('Saha kuyruğu beklemede. Bağlantı geri geldiğinde otomatik senkronize edilecek.');
      return;
    }

    try {
      const [genericResult, photoResult] = await Promise.all([
        processOfflineQueue(),
        processOfflinePhotoQueue(),
      ]);
      const totalProcessed = Number(genericResult.processed || 0) + Number(photoResult.processed || 0);
      const totalRemaining = Number(genericResult.remaining || 0) + Number(photoResult.remaining || 0);
      setOfflineQueueCount(totalRemaining);
      if (totalProcessed > 0) {
        setOfflineSyncMessage(`${totalProcessed} bekleyen saha kaydı sunucuya gönderildi.`);
      } else if (totalRemaining > 0) {
        setOfflineSyncMessage(`${totalRemaining} saha kaydı hâlâ kuyrukta bekliyor.`);
      } else {
        setOfflineSyncMessage('Bekleyen saha kaydı yok.');
      }
    } catch (error) {
      setOfflineSyncMessage('Saha kuyruğu senkronize edilirken hata oluştu.');
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, 60000);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  useEffect(() => {
    const refreshOfflineCount = async () => {
      const [genericCount, photoCount] = await Promise.all([
        Promise.resolve(getOfflineQueueCount()),
        getOfflinePhotoQueueCount(),
      ]);
      setOfflineQueueCount(Number(genericCount || 0) + Number(photoCount || 0));
    };

    refreshOfflineCount();
    const unsubscribeGeneric = subscribeOfflineQueue(() => { refreshOfflineCount(); });
    const unsubscribePhoto = subscribeOfflinePhotoQueue(() => { refreshOfflineCount(); });
    const handleOnline = () => {
      syncOfflineQueueNow();
    };

    window.addEventListener('online', handleOnline);
    if (window.navigator.onLine) {
      syncOfflineQueueNow();
    }

    return () => {
      unsubscribeGeneric();
      unsubscribePhoto();
      window.removeEventListener('online', handleOnline);
    };
  }, [syncOfflineQueueNow]);

  useEffect(() => {
    const updateViewportState = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobileView(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    updateViewportState();
    window.addEventListener('resize', updateViewportState);
    return () => window.removeEventListener('resize', updateViewportState);
  }, []);

  useEffect(() => {
    const userAgent = window.navigator.userAgent || '';
    const ios = /iphone|ipad|ipod/i.test(userAgent);
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    setIsIosDevice(ios);
    setIsStandaloneMode(standalone);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredInstallPrompt(event);
      setShowInstallButton(true);
      setShowInstallGuide(false);
    };

    const handleAppInstalled = () => {
      setDeferredInstallPrompt(null);
      setShowInstallButton(false);
      setShowInstallGuide(false);
      setIsStandaloneMode(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (isMobileView) {
      setSidebarOpen(false);
      setNotificationOpen(false);
    }
  }, [location.pathname, isMobileView]);

  useEffect(() => {
    const canManagePush = Boolean(user?.id);
    const supported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    const needsPermissionOrRetry = supported && (Notification.permission !== 'granted' || Boolean(pushPromptMessage));
    setCanPromptPush(canManagePush && needsPermissionOrRetry);
  }, [roleId, notificationOpen, pushPromptMessage, user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await apiClient.put(`${API_BASE}/notifications/${notificationId}/read`, {});
      await fetchNotifications();
    } catch (error) {
      // no-op
    }
  };

  const parseNotificationMetadata = (rawMetadata) => {
    if (!rawMetadata) return {};
    if (typeof rawMetadata === 'object') return rawMetadata;
    if (typeof rawMetadata === 'string') {
      try {
        const parsed = JSON.parse(rawMetadata);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        return {};
      }
    }
    return {};
  };

  const normalizeTargetPath = (rawPath) => {
    const path = String(rawPath || '').trim();
    if (!path) return '';
    if (path.startsWith('/#/')) return path.slice(2);
    if (path.startsWith('#/')) return path.slice(1);
    if (path.startsWith('/')) return path;
    return `/${path}`;
  };

  const resolveNotificationTarget = (entry) => {
    if (!entry) return '/';

    const relatedType = String(entry.related_type || '').toLowerCase();
    const relatedId = Number(entry.related_id || 0) || null;
    const metadata = parseNotificationMetadata(entry.metadata);
    const explicitTarget = normalizeTargetPath(metadata.target_url);
    if (explicitTarget) return explicitTarget;

    if (relatedType === 'task') {
      const taskId = Number(metadata.task_id || relatedId || 0);
      return taskId ? `/tasks?focusTask=${taskId}` : '/tasks';
    }
    if (relatedType === 'puantaj') return '/puantaj';
    if (relatedType === 'checklist_item') return '/checklists';
    if (relatedType === 'nonconformity') return '/nonconformities';
    if (relatedType === 'change_order') {
      const changeOrderId = Number(metadata.change_order_id || relatedId || 0);
      return changeOrderId ? `/change-orders?focusChangeOrder=${changeOrderId}` : '/change-orders';
    }
    if (relatedType === 'accounting') return '/accounting';
    if (relatedType === 'project') {
      const projectId = Number(metadata.project_id || relatedId || 0);
      return projectId > 0 ? `/projects/${projectId}` : '/projects';
    }
    return '/';
  };

  const handleNotificationClick = async (entry) => {
    if (!entry) return;
    await markNotificationRead(entry.id);
    setNotificationOpen(false);
    navigate(resolveNotificationTarget(entry));
  };

  const markAllNotificationsRead = async () => {
    try {
      await apiClient.put(`${API_BASE}/notifications/read-all`, {});
      await fetchNotifications();
    } catch (error) {
      // no-op
    }
  };

  const handleEnablePushFromPanel = async () => {
    setPushPromptBusy(true);
    setPushPromptMessage('');

    try {
      if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Bu cihaz web push bildirimini desteklemiyor.');
      }

      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }
      if (permission !== 'granted') {
        throw new Error('Bildirim izni verilmedi. Tarayıcı ayarlarından izin verin.');
      }

      const keyRes = await apiClient.get(`${API_BASE}/notifications/push-public-key`);
      const publicKey = String(keyRes.data?.public_key || '');
      if (!publicKey) {
        throw new Error('Sunucu push anahtarı eksik. Ayarlardan tekrar deneyin.');
      }

      const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register(swUrl);
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlToUint8Array(publicKey),
        });
      }

      await apiClient.post(
        `${API_BASE}/notifications/push-subscriptions`,
        { subscription: subscription.toJSON() }
      );

      setCanPromptPush(false);
      setPushPromptMessage('Cihaz bildirimi etkinleştirildi.');
    } catch (error) {
      setPushPromptMessage(error.response?.data?.message || error.message || 'Cihaz bildirimi etkinleştirilemedi.');
    } finally {
      setPushPromptBusy(false);
    }
  };

  const handleInstallApp = async () => {
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    await deferredInstallPrompt.userChoice;
    setDeferredInstallPrompt(null);
    setShowInstallButton(false);
  };

  const toggleInstallGuide = () => {
    setShowInstallGuide((prev) => !prev);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderIcon = (iconName) => {
    if (iconMode === 'emoji') {
      return <span className="nav-icon-emoji">{EMOJI_ICONS[iconName] || '?'}</span>;
    }
    return <AppIcon name={iconName} className="nav-icon-svg" />;
  };

  return (
    <div className="layout-container">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="brand-block">
            <img
              src={logoSrc}
              alt="Koç İnşaat logo"
              className="layout-logo"
              onError={() => setLogoSrc((current) => (current === logoFallback ? current : logoFallback))}
            />
            <div className="brand-copy">
              <h1 className="app-title">{BRAND_SHORT}</h1>
              <p className="app-subtitle">{BRAND_NAME}</p>
            </div>
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((prev) => !prev)}
            aria-label="Menüyü aç/kapat"
            aria-expanded={sidebarOpen}
            type="button"
          >
            <AppIcon name={sidebarOpen ? 'chevronLeft' : 'chevronRight'} className="sidebar-toggle-icon" />
          </button>
        </div>

        <nav className="sidebar-nav">
          {Object.entries(groupedNav).map(([section, items]) => (
            <div className="nav-section" key={section}>
              <h3 className="nav-section-title">{section}</h3>
              {items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`nav-link ${isActive(item.to) ? 'active' : ''}`}
                  title={!sidebarOpen ? item.label : undefined}
                  data-tooltip={!sidebarOpen ? item.label : ''}
                  aria-label={item.label}
                >
                  <span className="nav-icon">{renderIcon(item.icon)}</span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user?.username?.charAt(0).toUpperCase() || 'U'}</div>
            <div className="user-details">
              <p className="user-name">{user?.username}</p>
              <p className="user-email">{user?.email}</p>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout} type="button">
            Çıkış Yap
          </button>
        </div>
      </aside>

      {isMobileView && sidebarOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          aria-label="Menüyü kapat"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="main-wrapper">
        <header className="top-header">
          <div className="header-left">
            <button
              className="mobile-sidebar-toggle"
              onClick={() => setSidebarOpen((prev) => !prev)}
              aria-label="Menüyü aç/kapat"
              aria-expanded={sidebarOpen}
              type="button"
            >
              <AppIcon name="menu" className="sidebar-toggle-icon" />
            </button>
            <div className="breadcrumb">{BRAND_SHORT}</div>
          </div>

          <div className="header-right">
            {offlineQueueCount > 0 && (
              <button className="offline-queue-chip" type="button" onClick={syncOfflineQueueNow}>
                Saha Kuyruğu: {offlineQueueCount}
              </button>
            )}
            {!isStandaloneMode && showInstallButton && (
              <button className="install-app-btn" type="button" onClick={handleInstallApp}>
                Uygulamayı Yükle
              </button>
            )}

            {!isStandaloneMode && !showInstallButton && (
              <button className="install-help-btn" type="button" onClick={toggleInstallGuide}>
                Ana Ekrana Ekle
              </button>
            )}

            {!isStandaloneMode && showInstallGuide && (
              <div className="ios-install-hint">
                {isIosDevice
                  ? 'iPhone: Safari > Paylaş > Ana Ekrana Ekle'
                  : 'Tarayıcı menüsünden "Uygulamayı yükle" seçeneğini kullanın.'}
              </div>
            )}

            <div className="notification-wrapper">
              <button
                className="notification-btn"
                type="button"
                onClick={() => setNotificationOpen((prev) => !prev)}
                title="Bildirimler"
              >
                {iconMode === 'emoji' ? (
                  <span className="notification-emoji-icon">{EMOJI_ICONS.bell}</span>
                ) : (
                  <AppIcon name="bell" className="notification-bell-icon" />
                )}
                {unreadCount > 0 && <span className="notification-badge">{Math.min(unreadCount, 99)}</span>}
              </button>

              {notificationOpen && (
                <div className="notification-panel">
                  <div className="notification-panel-head">
                    <strong>Anımsatıcılar</strong>
                    <button type="button" onClick={markAllNotificationsRead}>Tümünü Okundu Yap</button>
                  </div>
                  {canPromptPush && (
                    <div className="notification-push-cta">
                      <span>Telefon bildirimi için cihaz izni gerekiyor.</span>
                      <button type="button" onClick={handleEnablePushFromPanel} disabled={pushPromptBusy}>
                        {pushPromptBusy ? 'İşleniyor...' : 'İzin Ver'}
                      </button>
                    </div>
                  )}
                  {pushPromptMessage && <div className="notification-push-hint">{pushPromptMessage}</div>}
                  {notifications.length === 0 ? (
                    <div className="notification-empty">Bildirim yok.</div>
                  ) : (
                    notifications.map((entry) => (
                      <button
                        type="button"
                        key={entry.id}
                        className={`notification-item ${entry.is_read ? 'read' : 'unread'}`}
                        onClick={() => handleNotificationClick(entry)}
                      >
                        <div className="notification-title">{entry.title}</div>
                        <div className="notification-message">{entry.message || '-'}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="header-info">Kullanıcı: {user?.username}</div>
          </div>
        </header>

        <main className="main-content">
          {offlineSyncMessage && <div className="offline-sync-banner">{offlineSyncMessage}</div>}
          <Outlet />
        </main>

        <footer className="app-footer">
          <p>&copy; 2026 {BRAND_NAME}. Tüm Hakları Saklıdır.</p>
        </footer>
      </div>
    </div>
  );
};

export default Layout;




